import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css";
import {
  FieldProps,
  getFieldContainerStyle,
} from "../InputFieldsController/InputFieldsController.utils";
import LabeledInputWrapper from "../InputFieldsController/LabeledInputWrapper";

interface EINInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
}

const formatEIN = (ein: string) => {
  const digits = ein.replace(/\D/g, "").slice(0, 9);
  const part1 = digits.slice(0, 2);
  const part2 = digits.slice(2, 9);
  return [part1, part2].filter(Boolean).join("-");
};

const maskEIN = (ein: string) => {
  const digits = ein.replace(/\D/g, "").slice(0, 9);

  if (digits.length <= 4) {
    return digits;
  }

  const visible = digits.slice(-3);
  return `**-****${visible}`;
};

const EINInput: React.FC<EINInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
}) => {
  const {
    id,
    value = "",
    placeholder,
    disabled,
    isValid = true,
    error,
    show = true,
    selected,
    example,
  } = field;

  const rawDigits = value.replace(/\D/g, "").slice(0, 9);
  const [showFull, setShowFull] = useState(false);
  const [inputBuffer, setInputBuffer] = useState(rawDigits);

  const containerStyle = getFieldContainerStyle(field);

  useEffect(() => {
    const cleaned = value.replace(/\D/g, "").slice(0, 9);
    setInputBuffer(cleaned);
  }, [value]);

  if (!show) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");

    if (digitsOnly.length > 9) return;

    setInputBuffer(digitsOnly);
    handleChange(id, { target: { value: digitsOnly } });
  };

  const displayValue = showFull
    ? formatEIN(inputBuffer)
    : maskEIN(inputBuffer);

  const handleFocus = () => {
    setShowFull(true);
    handleSelect(id);
  };

  return (
    <div style={containerStyle}>
      <LabeledInputWrapper field={field}>
        <div style={{ position: "relative" }}>
          <Form.Control
            id={id}
            maxLength={10}
            type="text"
            inputMode="numeric"
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            onChange={handleInputChange}
            onBlur={() => {
              handleBlur(id);
              setShowFull(false);
            }}
            onFocus={handleFocus}
            isInvalid={!isValid}
            className={`${CommonStyles.customInput} ${
              selected ? CommonStyles.selectedField : ""
            }`}
          />

          <span
            onClick={() => setShowFull((prev) => !prev)}
            style={{
              position: "absolute",
              top: "50%",
              right: 10,
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showFull ? <Icon.EyeSlash /> : <Icon.Eye />}
          </span>
        </div>

        {!isValid && error && (
          <Form.Control.Feedback type="invalid">
            {error}
          </Form.Control.Feedback>
        )}
      </LabeledInputWrapper>

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default EINInput;
